import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Package } from "lucide-react";
import { markOrderPlaced } from "../services/orderPayment";
import { issueGiftCard } from "../services/giftCards";
import { incrementCouponUse } from "../services/analytics";
import { redeemGiftCard } from "../services/giftCards";
import { useCart } from "../contexts/CartContext";
import { useApp } from "../contexts/AppContext";

/**
 * /success — Epoint redirects the buyer back to this page after a successful
 * card-entry. We perform three things:
 *   1. Flip the Firestore order from `awaiting_payment` → `placed`
 *   2. Execute the parked post-payment actions (issue gift cards, redeem
 *      coupon) that the CheckoutPage stored in localStorage before redirect.
 *   3. Clear the cart.
 *
 * Idempotent — refreshing the page does not re-issue gift cards.
 */
export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const { t } = useApp();
  const { clear } = useCart();
  const [status, setStatus] = useState("processing"); // processing | done | error
  const [errMsg, setErrMsg] = useState("");
  const [order, setOrder] = useState(null);
  const [issuedCodes, setIssuedCodes] = useState([]);

  // Epoint may pass back order_id either in the query string we baked in
  // (`?order_id=...`) or under various other keys depending on configuration.
  const orderId = params.get("order_id") || params.get("o") || params.get("tracking_no") || "";

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!orderId) {
        setStatus("error");
        setErrMsg("Sifariş nömrəsi tapılmadı.");
        return;
      }
      try {
        const txn = (() => {
          try { return localStorage.getItem(`epoint_txn_${orderId}`) || ""; } catch { return ""; }
        })();

        const found = await markOrderPlaced(orderId, txn ? { transaction: txn } : {});
        if (!found) {
          if (!cancelled) {
            setStatus("error");
            setErrMsg("Sifariş tapılmadı.");
          }
          return;
        }
        setOrder({ id: found.id, ...found.data });

        // Pull parked side-effects.
        let pending = null;
        try {
          const raw = localStorage.getItem(`epoint_pending_${orderId}`);
          pending = raw ? JSON.parse(raw) : null;
        } catch { /* ignore */ }

        // Only process pending actions the first time around (markOrderPlaced
        // is idempotent: if it returned an order that was already placed we
        // still want to skip duplicate side-effects).
        const alreadyProcessed = found.data.status && found.data.status !== "awaiting_payment";
        if (pending && !alreadyProcessed) {
          // 1) Coupon / gift-card redemption used at checkout
          if (pending.coupon) {
            try {
              if (pending.coupon.is_gift_card) {
                await redeemGiftCard(pending.coupon.id, pending.coupon.amount_azn || 0);
              } else {
                await incrementCouponUse(pending.coupon.id);
              }
            } catch (e) { console.warn("coupon redeem failed", e); }
          }

          // 2) Issue every gift card that was in the cart
          const codes = [];
          for (const gc of (pending.gift_card_items || [])) {
            try {
              const card = await issueGiftCard({
                template_id: gc.template_id,
                name: gc.name,
                amount_azn: gc.amount_azn,
                multi_use: gc.multi_use !== false,
                purchaser_user_id: gc.purchaser_user_id,
                purchaser_email: gc.purchaser_email,
                recipient_name: gc.recipient_name,
                recipient_email: gc.recipient_email,
                gift_message: gc.gift_message,
                order_id: pending.order_doc_id,
              });
              codes.push(card.code);
            } catch (e) { console.warn("gift card issue failed", e); }
          }
          if (codes.length) setIssuedCodes(codes);
        }

        // 3) Cleanup local storage + cart
        try {
          localStorage.removeItem(`epoint_pending_${orderId}`);
          localStorage.removeItem(`epoint_txn_${orderId}`);
        } catch { /* ignore */ }
        clear();

        if (!cancelled) setStatus("done");
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setStatus("error");
          setErrMsg(e?.message || "Naməlum xəta");
        }
      }
    }
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  return (
    <div className="bg-[var(--bg)] min-h-[70vh]">
      <section className="container-base py-20">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-[var(--surface)] border border-[var(--primary)] flex items-center justify-center text-[var(--primary)] mb-6">
            <CheckCircle2 size={42} />
          </div>
          <h1 className="font-display text-4xl md:text-5xl mb-4">Ödəniş uğurla tamamlandı</h1>

          {status === "processing" && (
            <p className="text-[var(--text-2)]">Sifarişiniz təsdiqlənir, bir az gözləyin…</p>
          )}

          {status === "done" && order && (
            <>
              <p className="text-[var(--text-2)] mb-6">
                Sifariş <span className="font-mono text-[var(--text)]">{order.tracking_no}</span> qəbul edildi.
                Yaxın zamanda sizinlə əlaqə saxlayacağıq.
              </p>
              {issuedCodes.length > 0 && (
                <div className="border border-[var(--primary)] bg-[var(--surface)] p-5 mb-6 text-left">
                  <div className="metadata mb-2">Hədiyyə kartı kod(ları)</div>
                  <ul className="space-y-1">
                    {issuedCodes.map((c) => (
                      <li key={c} className="font-mono text-lg tracking-widest">{c}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex flex-wrap justify-center gap-3">
                <Link to={`/orders/${order.tracking_no}`} className="btn-primary inline-flex items-center gap-2">
                  <Package size={16} /> Sifarişi izlə
                </Link>
                <Link to="/orders" className="btn-outline">Sifarişlərim</Link>
                <Link to="/catalog" className="btn-outline">{t.checkout.continueShopping || "Alış-verişə davam et"}</Link>
              </div>
            </>
          )}

          {status === "error" && (
            <>
              <p className="text-[var(--text-2)] mb-6">{errMsg}</p>
              <Link to="/catalog" className="btn-primary">Kataloqa qayıt</Link>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
